"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import { useAction, useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { Doc, Id } from "@/convex/_generated/dataModel";
import { BucketerLanding } from "./BucketerLanding";
import { BucketerConnect } from "./BucketerConnect";
import { BucketerProcessing } from "./BucketerProcessing";
import { BucketerResults } from "./BucketerResults";
import BetterEmailLogo from "@/components/logos/better-email-logo";
import { Row } from "@/app/v2/_components/row/Row";
import { Column } from "@/app/v2/_components/column/Column";

type Step = "landing" | "connecting" | "processing" | "results";

interface BucketerAppProps {
  showCrmConnect?: boolean;
  onBack?: () => void;
  /** When provided, runs in authenticated mode — skips sign-up, auto-detects CRM */
  teamId?: Id<"teams">;
  /** Pre-existing CRM credential ID (skips connect step) */
  credentialId?: string;
  /** Hide the logo header (when embedded in an authenticated page) */
  hideHeader?: boolean;
  /** Called when the run completes (authenticated mode) */
  onComplete?: () => void;
  /** Latest run from the database */
  latestRunId?: Id<"bucketerRuns">;
}

export function BucketerApp({
  showCrmConnect = false,
  onBack,
  teamId,
  credentialId: existingCredentialId,
  hideHeader = false,
  latestRunId,
  onComplete,
}: BucketerAppProps) {
  // In authenticated mode with existing credential, skip straight to processing
  const initialStep: Step =
    (teamId && existingCredentialId) || latestRunId
      ? "processing"
      : showCrmConnect
        ? "connecting"
        : teamId
          ? "connecting"
          : "landing";

  const [step, setStep] = useState<Step>(initialStep);
  const [runId, setRunId] = useState<Id<"bucketerRuns"> | null>(latestRunId ?? null);
  const [error, setError] = useState<string | null>(null);
  const autoStartedRef = useRef(false);

  const startRun = useAction(api.bucketer.actions.startBucketerRun);
  const signUp = useAction(api.bucketer.authActions.signUpFromBucketer);

  const run = useQuery(api.bucketer.queries.getRunByIdQuery, latestRunId ? { runId: latestRunId } : "skip");

  useEffect(() => {
    if (run) {
      if (run.status === "complete") {
        setStep("results");
      } else {
        setStep("processing");
      }
    }
  }, [run]);

  // Auto-start run in authenticated mode with existing credential
  useEffect(() => {
    if (!teamId || !existingCredentialId || autoStartedRef.current || latestRunId) return;
    autoStartedRef.current = true;

    (async () => {
      try {
        setError(null);
        const result = await startRun({ credentialId: existingCredentialId });
        setRunId(result.runId);
        setStep("processing");
      } catch (err) {
        console.error("Failed to start bucketer run:", err);
        setError("Something went wrong starting the databank process. Please try again.");
      }
    })();
  }, [teamId, existingCredentialId, startRun, latestRunId]);

  const handleConnect = () => {
    setError(null);
    setStep("connecting");
  };

  const handleCredentialConnected = useCallback(
    async (credentialId: string) => {
      try {
        setError(null);
        if (teamId) {
          // Authenticated mode — skip sign-up, start run directly
          const result = await startRun({ credentialId });
          setRunId(result.runId);
          setStep("processing");
        } else {
          // Marketing mode — auto-create user account from CRM connection
          const signUpResult = await signUp({ credentialId });
          if (!signUpResult) {
            setError("Account setup is in progress. Please try again in a moment.");
            return;
          }
          const result = await startRun({ credentialId });
          setRunId(result.runId);
          setStep("processing");
        }
      } catch (error) {
        console.error("Failed to start bucketer run:", error);
        setError("Something went wrong connecting your CRM. Please try again.");
      }
    },
    [teamId, signUp, startRun],
  );

  const handleProcessingComplete = useCallback(() => {
    if (onComplete) {
      onComplete();
    } else {
      setStep("results");
    }
  }, [onComplete]);

  return (
    <Column wide className="h-full">
      {!hideHeader && (
        <Row className="w-full flex flex-row flex-none h-[48px] items-start ">
          <BetterEmailLogo />
        </Row>
      )}
      {error && <div className="mx-auto max-w-md rounded-lg bg-red-50 p-4 text-sm text-red-700">{error}</div>}
      {step === "landing" && <BucketerLanding onConnect={handleConnect} />}
      {step === "connecting" && <BucketerConnect onConnected={handleCredentialConnected} />}
      {step === "processing" && runId && (
        <BucketerProcessing runId={runId} onComplete={handleProcessingComplete} onBack={onBack} />
      )}
      {step === "results" && runId && <BucketerResults runId={runId} />}
    </Column>
  );
}
